import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import { 
  Globe, 
  Trash2, 
  ExternalLink, 
  Power, 
  Facebook, 
  Instagram, 
  Twitter, 
  Linkedin,
  Youtube
} from 'lucide-react';
import Icon from '../../../components/AppIcon';


const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube
};

export default function BrandCard({ brand, onDelete, onToggleActive }) {
  const navigate = useNavigate();
  
  const Icon = Globe;
  
  const connectedAccounts = brand?.socialAccounts?.filter(acc => acc?.isConnected) || [];
  const totalFollowers = connectedAccounts?.reduce((sum, acc) => sum + acc?.followerCount, 0);

  const handleViewDetails = () => {
    // Add validation to prevent navigation with invalid brandId
    if (!brand?.id) {
      console.error('Cannot navigate: brand ID is undefined', brand);
      return;
    }
    navigate(`/brand-management/${brand?.id}`);
  };

  return (
    <div className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 ${!brand?.isActive ? 'opacity-60' : ''}`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{brand?.name}</h3>
            {brand?.industry && (
              <span className="text-sm text-gray-500">{brand?.industry}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleActive(brand?.id)}
            title={brand?.isActive ? 'Deactivate' : 'Activate'}
          >
            <Power className={`w-4 h-4 ${brand?.isActive ? 'text-green-600' : 'text-gray-400'}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(brand?.id)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {brand?.description && (
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{brand?.description}</p>
      )}
      {brand?.website && (
        <a
          href={brand?.website}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 mb-4"
        >
          <ExternalLink className="w-4 h-4" />
          Visit Website
        </a>
      )}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-medium text-gray-700">Connected Platforms</span>
          <span className="text-xs text-gray-500">{connectedAccounts?.length} connected</span>
        </div>
        
        {connectedAccounts?.length > 0 ? (
          <>
            <div className="flex gap-2 mb-3">
              {connectedAccounts?.map((account) => {
                const PlatformIcon = platformIcons?.[account?.platform] || Globe;
                return (
                  <div
                    key={account?.id}
                    className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                    title={`${account?.platform}: ${account?.accountName}`}
                  >
                    <PlatformIcon className="w-4 h-4 text-gray-600" />
                  </div>
                );
              })}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{totalFollowers?.toLocaleString()}</span> total followers
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">No social accounts connected</p>
        )}
      </div>
      <Button
        onClick={handleViewDetails}
        className="w-full mt-4"
        variant="outline"
      >
        Manage Brand
      </Button>
    </div>
  );
}